import React, { FC } from 'react';
import { ComponentRendering, isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import TestimonialButtons from './components/buttons';
import TestimonialMedia from './components/media';
import { ProductSeriesDetailTestimonialModel } from './models/productSeriesDetailTestimonial.model';
import { TProductSeriesDetailTestimonialProps } from './types/productSeriesDetailTestimonial.types';
import TextComponentDecider from '../@shared/TextComponentDecider';
import { getTextType } from '../../utils/getTextType.utils';
import {
  descriptionKeySeriesTestimonials,
  eyebrowKeySeriesTestimonials,
  titleKeySeriesTestimonials,
} from '../../constants/dictionary.constants';

const ProductSeriesDetailTestimonial: FC<TProductSeriesDetailTestimonialProps> = (props) => {
  const isFieldsEmpty = props?.fields && Object.keys(props?.fields).length === 0;
  const apiResponse = !isFieldsEmpty && isEmptyResponse(props);
  const model = new ProductSeriesDetailTestimonialModel(props);
  const imagePosition = props?.params?.imagePosition;
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <AnalyticsVisibleEvent rendering={props?.rendering as ComponentRendering} title={model?.title?.value ?? ''} template="Blog">
      <section className="product-series-testimonial">
        <div className={'product-series-testimonial-media__wrapper ' + (imagePosition === 'right' ? ' image-right' : '')}>
          <TestimonialMedia model={model} />
        </div>
        <div className={'product-series-testimonial-text-content__wrapper'}>
          <TextComponentDecider
            type={getTextType('Text', !model?.eyebrow?.value)}
            tag={'p'}
            field={{ value: model?.eyebrow?.value }}
            className="product-series-testimonial__eyebrow"
            dictionaryKey={eyebrowKeySeriesTestimonials}
          />

          <TextComponentDecider
            type={getTextType('Text', !model.title?.value)}
            tag={'h2'}
            field={{ value: model.title?.value }}
            className="product-series-testimonial__title"
            dictionaryKey={titleKeySeriesTestimonials}
          />

          <TextComponentDecider
            type={getTextType('RichText', !model.description?.value)}
            tag={'div'}
            field={{ value: model.description?.value }}
            className="product-series-testimonial__description rich-text"
            dictionaryKey={descriptionKeySeriesTestimonials}
          />
          <TestimonialButtons model={model} />
        </div>
      </section>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(ProductSeriesDetailTestimonial);
